import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import GlobalMap from '../../dashboard/components/GlobalMap';
import { useDashboardData } from '../../dashboard/hooks/useDashboardData';
import type { TimeRangeType } from '../../dashboard/hooks/useDashboardData';
import { useAuth } from '../../../context/AuthContext';
import { fetchEnterpriseProfile } from '../../../api/enterpriseApi';
import { fetchDashboardOverview } from '../../../api/dashboardApi';
import { apiRequest } from '../../../api/client';
import { entitledCodes } from '../../../utils/jurisdictionScope';
import type { EnterpriseProfile } from '../../../api/enterpriseApi';
import { Eyebrow, PageHeading } from '../../../design-system';
import styles from './EnterpriseContentPage.module.css';

export default function EnterpriseMapPage() {
  const { token } = useAuth();
  const [selectedTimeRange] = useState<TimeRangeType>('30D');
  const [selectedJurisdiction] = useState('All');
  const [selectedCountry] = useState('');
  const [selectedContinent] = useState('All');
  const [activeFeedTab] = useState('dashboard');
  const [profile, setProfile] = useState<EnterpriseProfile | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  // SRS #16: entitled jurisdictions → map codes; a locked jurisdiction leads to pricing.
  const [entitledNames, setEntitledNames] = useState<string[] | null>(null);
  const [countries, setCountries] = useState<{ name: string; iso2: string }[]>([]);
  useEffect(() => {
    apiRequest<{ id: string; name: string; iso2: string }[]>('/api/v1/dashboard/countries').then(setCountries).catch(() => {});
    if (!token) return;
    fetchDashboardOverview(token).then(o => setEntitledNames(o.scope.entitled_jurisdictions)).catch(() => {});
  }, [token]);
  const entitledIso = useMemo(() => entitledCodes(entitledNames, countries), [entitledNames, countries]);
  const shellBase = location.pathname.split('/dashboard')[0] + '/dashboard';

  const getDateRangeForAPI = useCallback(() => {
    if (selectedTimeRange === 'All') return {};
    if (selectedTimeRange === '24H') return { days: 1 };
    if (selectedTimeRange === '7D') return { days: 7 };
    if (selectedTimeRange === '30D') return { days: 30 };
    if (selectedTimeRange === '90D') return { days: 90 };
    if (selectedTimeRange === '1Y') return { days: 365 };
    return {};
  }, [selectedTimeRange]);

  const { jurisdictionStats, countryStats, isLoadingStats } = useDashboardData(token, {
    selectedJurisdiction,
    selectedTimeRange,
    dateFilterType: 'last_action_date',
    customStartDate: '',
    customEndDate: '',
    selectedCountry,
    selectedContinent,
    activeFeedTab,
    getDateRangeForAPI,
  });

  // Fetch company profile to get jurisdiction data
  useEffect(() => {
    fetchEnterpriseProfile()
      .then(setProfile)
      .catch(() => setProfile(null));
  }, []);

  // Get company's jurisdictions for map markers
  const getCompanyJurisdictions = () => {
    if (!profile) return [];
    const jurisdictions = [];
    
    // Add primary jurisdiction
    if (profile.jurisdiction) {
      jurisdictions.push(profile.jurisdiction);
    }
    
    // Add active jurisdictions
    if (profile.other_jurisdictions) {
      jurisdictions.push(...profile.other_jurisdictions);
    }
    
    // Clean up jurisdiction names (remove "Country – Country" format)
    const cleanedJurisdictions = jurisdictions.map(j => {
      // If format is "Country – Country", extract just the country name
      if (j.includes(' – ')) {
        return j.split(' – ')[0];
      }
      return j;
    });
    
    const result = Array.from(new Set(cleanedJurisdictions)); // Remove duplicates and convert to array
    console.log('Company jurisdictions for map:', result); // Debug log
    return result;
  };

  return (
    <div className={styles.page}>
      <Eyebrow tone="default" dot>COVERAGE</Eyebrow>
      <PageHeading
        accentWord="Regulation Map"
        accentTone="orange"
        size="h2"
        align="left"
        level="h2"
        className={styles.title}
      >
        Global Regulation Map
      </PageHeading>
      <GlobalMap
        stats={jurisdictionStats}
        countryStats={countryStats}
        isLoading={isLoadingStats}
        companyJurisdictions={getCompanyJurisdictions()}
        showCompanyMarkers={true}
        allowedCountryCodes={entitledIso}
        onCountryClick={(code, name) => navigate(`${shellBase}/regulations?jurisdiction=${encodeURIComponent(name)}&iso2=${encodeURIComponent(code)}`)}
        onLockedClick={(_code, name) => navigate('/pricing', { state: { changeOnly: true, reason: `${name} is not in your plan` } })}
      />
    </div>
  );
}
